import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useBranding, BrandLogo } from "@/context/BrandingContext";
import { ArrowRight, ScanSearch, Filter, Sparkles, FileSearch, Zap, Building2, BriefcaseBusiness, HeartHandshake, CheckCircle2, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1568992688065-536aad8a12f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjB0ZWFtd29ya3xlbnwwfHx8fDE3Nzg3ODY5NDZ8MA&ixlib=rb-4.1.0&q=85";

const DEFAULT_FEATURES = [
    {
        icon: Sparkles,
        title: "AI Resume Parsing",
        body: "Drop a PDF, get a fully structured candidate — skills, experience, gaps, summary — in seconds.",
    },
    {
        icon: ScanSearch,
        title: "Match Score Engine",
        body: "Every candidate is automatically scored against the job description. Top matches surface instantly so you spend time on the right people.",
    },
    {
        icon: Filter,
        title: "Red Flag Detection",
        body: "Define the red flags you care about with checkboxes. We flag them with severity and evidence.",
    },
    {
        icon: FileSearch,
        title: "Resume Gaps & Omissions",
        body: "Surfaces employment gaps, missing info, and inconsistent timelines before you call the candidate.",
    },
    {
        icon: Zap,
        title: "Interview Questions",
        body: "Custom interview prompts generated per candidate based on resume + JD weaknesses.",
    },
    {
        icon: Building2,
        title: "Company Workspace",
        body: "Invite recruiters, manage jobs, build a private candidate database that grows with every upload.",
    },
];

const FEATURE_ICONS = [Sparkles, ScanSearch, Filter, FileSearch, Zap, Building2, BriefcaseBusiness, HeartHandshake];

/** Substitute {product_name} / {short_name} placeholders in saved copy. */
function subst(str, branding) {
    if (!str || typeof str !== "string") return str || "";
    return str
        .replace(/\{product_name\}/g, branding.product_name || "")
        .replace(/\{short_name\}/g, branding.short_name || "");
}

export default function Landing() {
    const [featured, setFeatured] = useState([]);
    const [cc, setCc] = useState(null); // platform landing copy (admin-editable)
    const branding = useBranding();
    useEffect(() => {
        api.get("/public/featured-jobs").then((r) => setFeatured(r.data || [])).catch(() => {});
        api.get("/settings/landing-content").then((r) => setCc(r.data?.content || {})).catch(() => setCc({}));
    }, []);

    // Resolved copy with fallbacks to defaults
    const hero = cc?.hero || {};
    const features = (cc?.features && cc.features.length ? cc.features : DEFAULT_FEATURES).map((f, i) => ({
        icon: f.icon || FEATURE_ICONS[i % FEATURE_ICONS.length],
        title: subst(f.title, branding),
        body: subst(f.body, branding),
    }));
    const howSteps = (cc?.how_steps && cc.how_steps.length) ? cc.how_steps.map((s) => ({
        n: s.n,
        title: subst(s.title, branding),
        body: subst(s.body, branding),
    })) : [
        { n: "01", title: "Define the job", body: "Title, JD, must-haves, required skills, and red flags you want flagged automatically." },
        { n: "02", title: "Bulk upload resumes", body: "Drag in 50 PDFs. We parse, deduplicate, store securely and add to your candidate DB." },
        { n: "03", title: "Open the job", body: "Two-panel view: ranked candidates on the left, JD + AI analysis on the right." },
    ];

    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* Top nav */}
            <header className="border-b border-border bg-white">
                <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="landing-brand">
                        <BrandLogo size={28} />
                        <span className="font-display font-black text-lg tracking-tight">{branding.short_name}</span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
                        <a href="#features" className="hover:text-ikb transition-colors">Features</a>
                        <a href="#how" className="hover:text-ikb transition-colors">How it works</a>
                        <a href="#enquiry" className="hover:text-ikb transition-colors">Managed hiring</a>
                        <Link to="/managed-recruitment" className="hover:text-ikb transition-colors hidden lg:inline" data-testid="landing-nav-managed">More about managed</Link>
                        <a href="#jobs" className="hover:text-ikb transition-colors">Live jobs</a>
                    </nav>
                    <div className="flex items-center gap-2">
                        <Link to="/login"><Button variant="ghost" className="rounded-none" data-testid="landing-login-btn">Sign in</Button></Link>
                        <Link to="/signup"><Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="landing-signup-btn">Start hiring →</Button></Link>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section id="hero" className="border-b border-border relative overflow-hidden">
                <div className="grid-overlay absolute inset-0 pointer-events-none opacity-50" />
                <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12 sm:py-20 md:py-32 grid md:grid-cols-12 gap-8 md:gap-12 relative">
                    <div className="md:col-span-7 fade-up">
                        <div className="inline-flex items-center gap-2 border border-border bg-white px-3 py-1 text-xs font-mono uppercase tracking-widest mb-8">
                            <span className="w-1.5 h-1.5 bg-ikb"></span>
                            {subst(hero.badge, branding) || "For modern hiring teams · Built in India"}
                        </div>
                        <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
                            {subst(hero.headline_line1, branding) || "Screen 200 resumes"}<br />
                            {subst(hero.headline_line2, branding) || "in the time it took"}<br />
                            {subst(hero.headline_line3, branding) || "to read"} <span className="text-ikb">{subst(hero.headline_accent, branding) || "one."}</span>
                        </h1>
                        <p className="mt-8 text-lg text-muted-foreground max-w-xl leading-relaxed">
                            {subst(hero.subtext, branding) || `${branding.product_name} is the AI applicant tracking system built for recruiters who refuse to drown in PDFs. Upload resumes. Get match scores, red flags, gaps and interview questions — instantly.`}
                        </p>
                        <div className="mt-10 flex items-center gap-4">
                            <Link to="/signup">
                                <Button data-testid="hero-cta-signup" className="rounded-none bg-ikb hover:bg-[#001f7a] text-white px-6 py-6 text-base">
                                    {subst(hero.primary_cta, branding) || "Create company account"} <ArrowRight className="ml-2 w-4 h-4" />
                                </Button>
                            </Link>
                            <Link to="/login">
                                <Button variant="outline" className="rounded-none border-foreground text-foreground px-6 py-6 text-base hover:bg-foreground hover:text-background" data-testid="hero-cta-login">
                                    {subst(hero.secondary_cta, branding) || "I have an account"}
                                </Button>
                            </Link>
                        </div>
                        <div className="mt-12 flex items-center gap-8 text-xs uppercase tracking-widest font-mono text-muted-foreground">
                            <div><span className="font-bold text-foreground text-base">{hero.stat_1_value || "10×"}</span> {hero.stat_1_label || "faster screening"}</div>
                            <div><span className="font-bold text-foreground text-base">{hero.stat_2_value || "0"}</span> {hero.stat_2_label || "manual data entry"}</div>
                            <div><span className="font-bold text-foreground text-base">{hero.stat_3_value || "∞"}</span> {hero.stat_3_label || "candidate database"}</div>
                        </div>
                    </div>
                    <div className="md:col-span-5 relative">
                        <div className="absolute -top-4 -left-4 right-4 bottom-4 bg-ikb -z-0"></div>
                        <img src={HERO_IMG} alt="recruiting team" className="relative w-full h-[480px] object-cover" />
                    </div>
                </div>
            </section>

            {/* Managed recruitment — Enquiry section (moved above features for visibility) */}
            <EnquirySection brandingShort={branding.short_name} brandingName={branding.product_name} cc={cc || {}} />

            {/* Features */}
            <section id="features" className="border-b border-border">
                <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-14 sm:py-24">
                    <div className="flex items-end justify-between mb-16">
                        <div>
                            <div className="text-xs uppercase tracking-widest font-mono text-ikb mb-3">01 — Capabilities</div>
                            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight max-w-2xl">
                                {subst(cc?.features_heading, branding) || "The screening layer your ATS forgot to ship."}
                            </h2>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-border">
                        {features.map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <div key={f.title + i} className="bg-white p-8 hover:bg-secondary transition-colors group">
                                    <Icon className="w-7 h-7 text-ikb mb-6" />
                                    <h3 className="font-display font-bold text-xl mb-3">{f.title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section id="how" className="border-b border-border bg-foreground text-background">
                <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-14 sm:py-24">
                    <div className="text-xs uppercase tracking-widest font-mono text-[#7faaff] mb-3">02 — Workflow</div>
                    <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-16 max-w-2xl">
                        {subst(cc?.how_heading, branding) || "Three steps. From inbox flood to interview shortlist."}
                    </h2>
                    <div className="grid md:grid-cols-3 gap-px bg-white/10">
                        {howSteps.map((s, i) => (
                            <div key={(s.n || "") + i} className="bg-foreground p-10">
                                <div className="font-mono text-xs text-[#7faaff]">{s.n}</div>
                                <h3 className="font-display font-bold text-2xl mt-6 mb-3">{s.title}</h3>
                                <p className="text-sm text-white/70 leading-relaxed">{s.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Live jobs */}
            <section id="jobs" className="border-b border-border">
                <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-14 sm:py-24">
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb mb-3">03 — Live on platform</div>
                    <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight mb-12">
                        {subst(cc?.jobs_heading, branding) || `Open roles from companies hiring on ${branding.short_name}.`}
                    </h2>
                    {featured.length === 0 ? (
                        <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
                            No public openings yet. Be the first company to publish here.
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 gap-px bg-border">
                            {featured.map((j) => (
                                <div key={j.id} className="bg-white p-6 hover:bg-secondary transition-colors">
                                    <div className="flex items-start justify-between gap-4 mb-3">
                                        <h3 className="font-display font-bold text-lg leading-tight">{j.title}</h3>
                                        <span className="text-[10px] font-mono uppercase tracking-widest border border-border px-2 py-0.5">{j.employment_type || "Full-time"}</span>
                                    </div>
                                    <div className="text-sm text-muted-foreground mb-2">{j.company_name}</div>
                                    <div className="flex items-center gap-3 text-xs text-muted-foreground font-mono">
                                        {j.location && <span>📍 {j.location}</span>}
                                        {j.seniority && <span>· {j.seniority}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* CTA */}
            <section className="border-b border-border">
                <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-14 sm:py-24 grid md:grid-cols-12 gap-8 md:gap-12 items-center">
                    <div className="md:col-span-7">
                        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight">
                            {subst(cc?.cta_headline_line1, branding) || "Stop reading resumes."}<br />
                            {subst(cc?.cta_headline_line2, branding) || "Start"} <span className="text-ikb">{subst(cc?.cta_headline_accent, branding) || "comparing"}</span> {subst(cc?.cta_headline_line3, branding) || "candidates."}
                        </h2>
                        <p className="mt-6 text-muted-foreground max-w-xl">
                            {subst(cc?.cta_subtext, branding) || "Free to start. No credit card. Invite your recruiting team in under a minute."}
                        </p>
                    </div>
                    <div className="md:col-span-5 flex md:justify-end">
                        <Link to="/signup">
                            <Button data-testid="cta-bottom-signup" className="rounded-none bg-ikb hover:bg-[#001f7a] text-white px-8 py-7 text-base">
                                {subst(cc?.cta_button, branding) || "Create company account"} →
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="bg-white">
                <div className="max-w-[1600px] mx-auto px-6 py-8 flex items-center justify-between text-xs font-mono text-muted-foreground">
                    <div>{subst(cc?.footer_copyright, branding) || `© 2026 ${branding.short_name} · Built for hiring teams`}</div>
                    <div>{subst(cc?.footer_tagline, branding) || "Built for recruiters who refuse to drown in PDFs"}</div>
                </div>
            </footer>
        </div>
    );
}

/** Section that pitches managed recruitment + captures hiring enquiries. */
export function EnquirySection({ brandingShort, brandingName, cc = {} }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", roles_count: "1-5", role_type: "Tech", timeline: "Within 1 month", message: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const branding = { product_name: brandingName, short_name: brandingShort };

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
        setBusy(true);
        try {
            await api.post("/portal/enquiries", { ...form, source: "landing" });
            setDone(true);
            toast.success("Got it — our team will reach out within one business day.");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not send. Try again or email us.";
            toast.error(typeof msg === "string" ? msg : "Could not send.");
        } finally { setBusy(false); }
    };

    const set = (k, v) => setForm({ ...form, [k]: v });

    const bullets = (cc.managed_bullets && cc.managed_bullets.length) ? cc.managed_bullets : [
        "Dedicated recruiter pod within 48 hours",
        "Sourcing from LinkedIn, Naukri, Apna + AI talent pool",
        "First shortlist in <72 hours for tech & ops roles",
        "Bulk hiring sprints — 50+ offers/month",
        "Pay-per-hire · no setup or retainer fees",
        "All data lives in your {short_name} workspace — handover anytime",
    ];

    return (
        <section id="enquiry" className="border-b border-border bg-secondary/40">
            <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-14 sm:py-24 grid md:grid-cols-12 gap-8 md:gap-12 items-start">
                <div className="md:col-span-5">
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb mb-3">{subst(cc.managed_section_label, branding) || "04 — Managed recruitment"}</div>
                    <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-tight">
                        {subst(cc.managed_heading_line1, branding) || "Hiring at"} <span className="text-ikb">{subst(cc.managed_heading_accent, branding) || "scale?"}</span><br />
                        {subst(cc.managed_heading_line2, branding) || "Let our recruiters run it for you."}
                    </h2>
                    <p className="mt-6 text-muted-foreground text-base leading-relaxed max-w-xl">
                        {subst(cc.managed_paragraph, branding) || `${brandingShort} also operates as a full-service recruitment partner. We staff your funnel — sourcing, screening, scheduling, candidate experience — and you stay in ${brandingName} to review and approve. Pay per hire, no retainers.`}
                    </p>
                    <ul className="mt-7 space-y-3 text-sm">
                        {bullets.map((line, i) => (
                            <li key={i} className="flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                                <span>{subst(line, branding).replace(/\{brandingShort\}/g, brandingShort)}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-8 flex items-center gap-6 text-xs uppercase tracking-widest font-mono text-foreground/70">
                        <div className="flex items-center gap-2"><BriefcaseBusiness className="w-4 h-4 text-ikb" /> 250+ roles closed</div>
                        <div className="flex items-center gap-2"><HeartHandshake className="w-4 h-4 text-ikb" /> 30+ companies</div>
                    </div>
                </div>
                <div className="md:col-span-7 bg-white border border-border p-5 sm:p-7">
                    {done ? (
                        <div className="py-10 text-center" data-testid="enquiry-success">
                            <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
                            <h3 className="font-display font-bold text-2xl mt-3">Thanks — we'll be in touch.</h3>
                            <p className="text-sm text-muted-foreground mt-2">A specialist from our team will reach out at <b>{form.email}</b> within one business day.</p>
                            <Link to="/signup" className="mt-6 inline-block">
                                <Button className="rounded-none bg-ikb hover:bg-[#001f7a] text-white">Start hiring on {brandingShort} →</Button>
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-3" data-testid="enquiry-form">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-ikb font-bold">Talk to our hiring team</div>
                            <h3 className="font-display font-bold text-xl">{subst(cc.managed_form_title, branding) || "Tell us what you're hiring for."}</h3>
                            <div className="grid sm:grid-cols-2 gap-3 pt-1">
                                <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name *" className="rounded-none h-11" data-testid="eq-name" required />
                                <Input value={form.email} onChange={(e) => set("email", e.target.value)} type="email" placeholder="Work email *" className="rounded-none h-11" data-testid="eq-email" required />
                                <Input value={form.company} onChange={(e) => set("company", e.target.value)} placeholder="Company" className="rounded-none h-11" data-testid="eq-company" />
                                <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone (optional)" className="rounded-none h-11" data-testid="eq-phone" />
                                <select value={form.roles_count} onChange={(e) => set("roles_count", e.target.value)} className="h-11 px-2 border border-border bg-white text-sm" data-testid="eq-roles">
                                    <option>1-5 roles</option>
                                    <option>5-20 roles</option>
                                    <option>20-50 roles</option>
                                    <option>50+ (bulk hiring)</option>
                                </select>
                                <select value={form.role_type} onChange={(e) => set("role_type", e.target.value)} className="h-11 px-2 border border-border bg-white text-sm" data-testid="eq-roletype">
                                    <option>Tech</option>
                                    <option>Sales / GTM</option>
                                    <option>Operations</option>
                                    <option>Customer success</option>
                                    <option>BPO / Bulk hiring</option>
                                    <option>Executive / leadership</option>
                                    <option>Other</option>
                                </select>
                            </div>
                            <select value={form.timeline} onChange={(e) => set("timeline", e.target.value)} className="w-full h-11 px-2 border border-border bg-white text-sm" data-testid="eq-timeline">
                                <option>Immediate (this week)</option>
                                <option>Within 1 month</option>
                                <option>Within 3 months</option>
                                <option>Just exploring</option>
                            </select>
                            <Textarea value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Roles, locations, budget, anything else…" className="rounded-none min-h-[90px]" data-testid="eq-message" />
                            <Button type="submit" disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white w-full h-11" data-testid="eq-submit">
                                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <>Request a callback <ArrowRight className="w-4 h-4 ml-2" /></>}
                            </Button>
                            <p className="text-[10px] font-mono text-foreground/50 text-center">By submitting you agree we may contact you about your hiring needs. No spam.</p>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
}
